import * as Yup from "yup"

import {
    CVR_LENGTH,
    EAN_LENGTH,
    MAX_EMAIL_LENGTH,
    MAX_NAME_LENGTH,
    POSTAL_CODE_REGEX,
} from "../../../../constants/inputValidationDefaults"
import type { AppLocalization } from "../../../../modules/localization/appLocalization"
import { BillReceiverType } from "../../../../modules/company/admin/organization/userGroups/userGroupsTypes"

import { BillReceiversType } from "../billReceiverTypes"

type L10n = Pick<AppLocalization, "getString">

function isDigitsOfExactLength(value: string, length: number): boolean {
    return new RegExp(`^\\d{${length}}$`, "u").test(value.trim())
}

export function getBillReceiverSearchValidationError({
    billType,
    search,
    l10n,
}: Readonly<{
    billType: BillReceiversType | null
    search: string
    l10n: L10n
}>): string | undefined {
    const trimmed = search.trim()
    if (!trimmed || !billType) {
        return
    }

    if (billType === BillReceiversType.PRIVATE_PERSON) {
        const emailLabel = l10n.getString("bill-receivers-form-error-label-email")
        if (trimmed.length > MAX_EMAIL_LENGTH) {
            return l10n.getString("bill-receivers-validation-max-length", { label: emailLabel, max: MAX_EMAIL_LENGTH })
        }
        if (!Yup.string().email().isValidSync(trimmed)) {
            return l10n.getString("bill-receivers-validation-email-invalid")
        }
    }

    if (billType === BillReceiversType.PUBLIC_INSTITUTION && !isDigitsOfExactLength(trimmed, EAN_LENGTH)) {
        return l10n.getString("bill-receivers-validation-ean-13-digits")
    }

    if (billType === BillReceiversType.PRIVATE_COMPANY && !isDigitsOfExactLength(trimmed, CVR_LENGTH)) {
        return l10n.getString("bill-receivers-validation-cvr-8-digits")
    }

    return
}

export function getSendInvoiceCompanyValidationError({
    invoiceType,
    search,
    l10n,
}: Readonly<{
    invoiceType: BillReceiverType
    search: string
    l10n: L10n
}>): string | undefined {
    const trimmed = search.trim()
    if (!trimmed) {
        return
    }

    if (invoiceType === BillReceiverType.EMAIL_ADDRESS) {
        const emailLabel = l10n.getString("bill-receivers-form-error-label-email")
        if (trimmed.length > MAX_EMAIL_LENGTH) {
            return l10n.getString("bill-receivers-validation-max-length", { label: emailLabel, max: MAX_EMAIL_LENGTH })
        }
        if (!Yup.string().email().isValidSync(trimmed)) {
            return l10n.getString("bill-receivers-validation-email-invalid")
        }
    }

    if (invoiceType === BillReceiverType.EAN_NUMBER && !isDigitsOfExactLength(trimmed, EAN_LENGTH)) {
        return l10n.getString("bill-receivers-validation-ean-13-digits")
    }

    return
}

export function createBillReceiverDetailsSchemas(l10n: L10n): Readonly<{
    privatePerson: Yup.AnyObjectSchema
    publicInstitution: Yup.AnyObjectSchema
    privateCompany: Yup.AnyObjectSchema
}> {
    const requiredMessage = (label: string) => l10n.getString("create-order-validation-required", { label })
    const maxLengthMessage = (label: string, max: number) =>
        l10n.getString("bill-receivers-validation-max-length", { label, max })

    const labelCountry = l10n.getString("bill-receivers-form-error-label-country")
    const labelDebtorNumber = l10n.getString("bill-receivers-form-error-label-debtor-number")
    const labelAddressLine1 = l10n.getString("bill-receivers-form-error-label-address-line-1")
    const labelAddressLine2 = l10n.getString("bill-receivers-form-error-label-address-line-2")
    const labelPostalCode = l10n.getString("bill-receivers-form-error-label-postal-code")
    const labelCity = l10n.getString("bill-receivers-form-error-label-city")
    const labelTelephone = l10n.getString("bill-receivers-form-error-label-telephone")
    const labelName = l10n.getString("bill-receivers-form-error-label-name")
    const labelInstitutionName = l10n.getString("bill-receivers-form-error-label-institution-name")
    const labelEmail = l10n.getString("bill-receivers-form-error-label-email")
    const labelContactPerson = l10n.getString("bill-receivers-form-error-label-contact-person")
    const labelCompanyName = l10n.getString("bill-receivers-form-error-label-company-name")

    const baseSchemaFields = Yup.object().shape({
        country: Yup.string().required(requiredMessage(labelCountry)).label(labelCountry),
        debtorNumber: Yup.string().label(labelDebtorNumber),
        addressLine1: Yup.string().required(requiredMessage(labelAddressLine1)).label(labelAddressLine1),
        addressLine2: Yup.string().label(labelAddressLine2),
        postalCode: Yup.string()
            .required(requiredMessage(labelPostalCode))
            .matches(POSTAL_CODE_REGEX, l10n.getString("bill-receivers-validation-postal-code-4-digits"))
            .label(labelPostalCode),
        city: Yup.string()
            .required(requiredMessage(labelCity))
            .max(MAX_NAME_LENGTH, maxLengthMessage(labelCity, MAX_NAME_LENGTH))
            .label(labelCity),
        telephone: Yup.string()
            .required(requiredMessage(labelTelephone))
            .matches(/^[0-9+\-() ]+$/u, l10n.getString("bill-receivers-validation-telephone-invalid"))
            .label(labelTelephone),
    })

    const baseSchemaWithContactFields = baseSchemaFields.shape({
        email: Yup.string()
            .required(requiredMessage(labelEmail))
            .max(MAX_EMAIL_LENGTH, maxLengthMessage(labelEmail, MAX_EMAIL_LENGTH))
            .email(l10n.getString("bill-receivers-validation-email-invalid"))
            .label(labelEmail),
        contactPerson: Yup.string()
            .required(requiredMessage(labelContactPerson))
            .max(MAX_NAME_LENGTH, maxLengthMessage(labelContactPerson, MAX_NAME_LENGTH))
            .label(labelContactPerson),
    })

    const privatePerson = baseSchemaFields.shape({
        name: Yup.string()
            .required(requiredMessage(labelName))
            .max(MAX_NAME_LENGTH, maxLengthMessage(labelName, MAX_NAME_LENGTH))
            .label(labelName),
    })

    const publicInstitution = baseSchemaWithContactFields.shape({
        vatnumber: Yup.string().matches(/^\d{8}$/u, {
            message: l10n.getString("bill-receivers-validation-cvr-8-digits"),
            excludeEmptyString: true,
        }),
        institutionName: Yup.string()
            .required(requiredMessage(labelInstitutionName))
            .max(MAX_NAME_LENGTH, maxLengthMessage(labelInstitutionName, MAX_NAME_LENGTH))
            .label(labelInstitutionName),
    })

    const privateCompany = baseSchemaWithContactFields.shape({
        companyName: Yup.string()
            .required(requiredMessage(labelCompanyName))
            .max(MAX_NAME_LENGTH, maxLengthMessage(labelCompanyName, MAX_NAME_LENGTH))
            .label(labelCompanyName),
    })

    return {
        privatePerson: privatePerson as unknown as Yup.AnyObjectSchema,
        publicInstitution: publicInstitution as unknown as Yup.AnyObjectSchema,
        privateCompany: privateCompany as unknown as Yup.AnyObjectSchema,
    }
}
